import { Fragment, type ReactNode } from "react"

import type { CompanyRunResearch } from "@/admin/monitoring/company-run-research-data"
import type { FileEvidenceTarget } from "@/admin/monitoring/file-evidence-data"
import {
  formatRunStart,
  humanizeKey,
  pluralize,
} from "@/admin/monitoring/run-presentation"
import { StoredDetails } from "@/admin/monitoring/stored-value"
import { dataByAssessmentOutcome } from "@/constants/terminal/assessment-outcome"
import { dataByIndividualRoleKind } from "@/constants/terminal/individual-role-kind"
import {
  dataBySearchSubjectKind,
  SearchSubjectKind,
} from "@/constants/terminal/search-subject-kind"
import { briefParagraphs } from "@/terminal/file/brief-paragraphs"

type RunParts = CompanyRunResearch["parts"]
type Brief = NonNullable<RunParts["brief"]>["brief"]

const AuditList = ({ title, items }: { title: string; items: ReactNode[] }) => (
  <div className="text-muted-foreground space-y-1 text-xs">
    <p>{title}</p>
    <ul className="list-disc space-y-1 pl-4">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  </div>
)

// What the Brief withheld, set out to argue, and how its sentences measured.
const BriefAudit = ({ brief }: { brief: Brief }) => (
  <>
    {(brief.uncitedModelClaimIndexes ??
      brief.meritModelClaimIndexes ??
      brief.coverageModelClaimIndexes) && (
      <p className="text-muted-foreground text-xs">
        Withheld: {brief.uncitedModelClaimIndexes?.length ?? 0} uncited model
        claims, {brief.meritModelClaimIndexes?.length ?? 0} merit claims,{" "}
        {brief.coverageModelClaimIndexes?.length ?? 0} coverage claims.
      </p>
    )}
    {brief.plan && (
      <AuditList
        title={`Plan: ${brief.plan.thesis}`}
        items={brief.plan.matters.map(
          ({ text, sourceRecordIds, sourceCheckIds = [] }) => (
            <>
              {text} (
              {sourceRecordIds.length > 0
                ? pluralize(sourceRecordIds.length, "record")
                : pluralize(sourceCheckIds.length, "negative search")}
              :{" "}
              <span className="break-all">
                {[...sourceRecordIds, ...sourceCheckIds].join(", ")}
              </span>
              )
            </>
          )
        )}
      />
    )}
    {brief.sentenceWarnings && (
      <AuditList
        title="Sentence warnings:"
        items={brief.sentenceWarnings.map(
          ({ kind, claimIndex, detail }) =>
            `${humanizeKey(kind)}${claimIndex === null ? "" : ` in claim ${claimIndex + 1}`}: ${detail}`
        )}
      />
    )}
  </>
)

// A Run's File parts: its Assessment's File Scope, possible matches and
// Individuals, and the Brief written for it, if one was.
export const FileView = ({
  assessment,
  brief,
  onOpenEvidence,
}: {
  assessment: NonNullable<RunParts["assessment"]>
  brief: RunParts["brief"]
  onOpenEvidence: (target: FileEvidenceTarget, title: string) => void
}) => {
  const fileScope = assessment.fileScope
  const possibleMatches = assessment.possibleMatches ?? []
  const individuals = assessment.individuals ?? []
  return (
    <div className="space-y-12 text-sm">
      <div className="grid items-start gap-8 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <section className="space-y-3 lg:sticky lg:top-6">
          <h2 className="text-sm font-semibold">Assessment</h2>
          <p>
            {assessment.outcome
              ? dataByAssessmentOutcome[assessment.outcome].label
              : "In progress"}
          </p>
          <dl className="text-muted-foreground grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
            {assessment.completedAt && (
              <>
                <dt>Completed</dt>
                <dd>{formatRunStart(assessment.completedAt)}</dd>
              </>
            )}
            <dt>Assessment</dt>
            <dd className="break-all">{assessment.id}</dd>
            {brief && (
              <>
                <dt>Brief written</dt>
                <dd>{formatRunStart(brief.createdAt)}</dd>
              </>
            )}
          </dl>
        </section>
        <section className="max-w-prose space-y-4">
          <h2 className="text-sm font-semibold">Brief</h2>
          {!brief && (
            <p className="text-muted-foreground">
              No Brief written for this Run: the buyer reads the Company’s
              earlier Brief, if it has one.
            </p>
          )}
          {brief && (
            <>
              <div className="space-y-4 font-serif text-base leading-relaxed">
                {briefParagraphs(brief.brief.claims).map((paragraph) => (
                  <p key={paragraph[0].index}>
                    {paragraph.map(({ claim, index }) => (
                      <Fragment key={index}>
                        {claim.text}{" "}
                        <button
                          aria-label={`Evidence for claim ${index + 1}`}
                          className="text-terminal-accent font-mono text-xs hover:underline"
                          onClick={() =>
                            onOpenEvidence(
                              { kind: "citation", index },
                              `Brief claim ${index + 1}`
                            )
                          }
                        >
                          [{index + 1}]
                        </button>{" "}
                      </Fragment>
                    ))}
                  </p>
                ))}
                {brief.brief.scopeSentence && (
                  <p>{brief.brief.scopeSentence}</p>
                )}
              </div>
              <BriefAudit brief={brief.brief} />
            </>
          )}
        </section>
      </div>
      {fileScope && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">File Scope</h2>
          {fileScope.note && <p className="max-w-prose">{fileScope.note}</p>}
          <h3 className="text-sm font-semibold">Recorded Company Scope</h3>
          <ul className="space-y-3">
            {fileScope.scopeEntries.map((entry) => (
              <li
                key={entry.companyScopeEntryId}
                className="space-y-1 border-l-2 pl-3"
              >
                <p className="break-all">
                  {dataBySearchSubjectKind[entry.subject.kind].label}{" "}
                  {entry.subject.kind === SearchSubjectKind.BRAND
                    ? entry.subject.brandId
                    : entry.subject.legalEntityId}
                </p>
                <p className="text-muted-foreground text-xs">
                  {entry.effectiveFrom} to {entry.effectiveTo ?? "present"}
                </p>
                <StoredDetails
                  summary="Stored Company Scope Entry"
                  value={entry}
                />
              </li>
            ))}
          </ul>
          {fileScope.reportedTransactions?.map((transaction, index) => (
            <p key={index}>{transaction.presentation}</p>
          ))}
          {fileScope.unsearchedCategories?.map(({ category, presentation }) => (
            <p key={category}>{presentation}</p>
          ))}
          {[
            ...(fileScope.unsearchedJurisdictions ?? []),
            ...(fileScope.deferredSearches ?? []),
            ...(fileScope.incompleteSearches ?? []),
          ].map((search) => (
            <p key={search.presentation}>{search.presentation}</p>
          ))}
          <StoredDetails
            summary="Acquisition Carryover and included history"
            value={{
              acquisitionCarryovers: fileScope.acquisitionCarryovers,
              includedPredecessorHistory: fileScope.includedPredecessorHistory,
            }}
          />
          <StoredDetails
            summary="Excluded categories and stored File Scope details"
            value={fileScope}
          />
        </section>
      )}
      <section className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Possible matches</h2>
          <p className="text-muted-foreground">
            Worth investigating. Possible matches don’t affect the Assessment.
          </p>
        </div>
        {possibleMatches.length === 0 && (
          <p className="text-muted-foreground">None.</p>
        )}
        {possibleMatches.map((match, index) => (
          <article className="space-y-2 border-l-2 pl-3" key={index}>
            <h3 className="font-semibold">{match.presentation.title}</h3>
            <p className="max-w-prose">{match.presentation.summary}</p>
            <StoredDetails
              summary="Stored Possible Match details"
              value={match}
            />
          </article>
        ))}
      </section>
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">File Individuals</h2>
        {individuals.length === 0 && (
          <p className="text-muted-foreground">None recorded.</p>
        )}
        {individuals.map((individual) => (
          <article
            className="space-y-2 border-l-2 pl-3"
            key={individual.individualId}
          >
            <h3 className="font-semibold">{individual.name}</h3>
            <ul className="space-y-1">
              {individual.roles.map((role, index) => (
                <li key={index}>
                  {role.sourceRole ??
                    dataByIndividualRoleKind[role.roleKind].label}{" "}
                  at {role.businessName}
                  {role.from && ` from ${role.from}`}
                  {role.to && ` to ${role.to}`}
                </li>
              ))}
            </ul>
            {individual.otherBusinesses.length > 0 && (
              <p className="text-muted-foreground">
                Other recorded businesses:{" "}
                {individual.otherBusinesses
                  .map((business) => business.name)
                  .join(", ")}
              </p>
            )}
            <StoredDetails
              summary="Stored Individual details"
              value={individual}
            />
          </article>
        ))}
      </section>
    </div>
  )
}
